<template>
  <a-modal
    :title="$t('设置搜索字段')"
    v-model:open="visible"
    width="1000px"
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <div class="search-config-container">
      <div class="table-wrapper left-table">
        <div class="table-title">
          {{ $t('所有字段') }}
          <a-input
            v-model:value="leftSearch"
            size="small"
            :placeholder="$t('搜索标签/字段名')"
            style="margin-top: 8px"
            allow-clear
          />
        </div>
        <s-table
          :columns="leftColumns"
          :data-source="filteredLeftData"
          :pagination="false"
          :row-selection="leftRowSelection"
          v-model:selectedRowKeys="leftSelectedRowKeys"
          row-key="field_name"
          size="small"
          :row-class-name="record => (record.is_selected ? 'row-selected' : '')"
          expand-row-by-click
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.dataIndex === 'label'">
              <span
                :style="{
                  color: record.is_selected ? '#ccc' : 'inherit',
                }"
              >
                {{ record.label }}
              </span>
            </template>
          </template>
        </s-table>
      </div>

      <div class="middle-btns">
        <a-button
          type="primary"
          shape="circle"
          :disabled="leftSelectedRowKeys.length === 0"
          @click="moveToRight"
        >
          <template #icon><RightOutlined /></template>
        </a-button>
        <a-button
          type="primary"
          shape="circle"
          :disabled="rightSelectedRowKeys.length === 0"
          @click="moveToLeft"
        >
          <template #icon><LeftOutlined /></template>
        </a-button>
      </div>

      <div class="table-wrapper right-table">
        <div class="table-title">
          {{ $t('已选字段') }}
          <a-input
            v-model:value="rightSearch"
            size="small"
            :placeholder="$t('搜索标签/字段名')"
            style="margin-top: 8px"
            allow-clear
          />
        </div>
        <s-table
          :columns="rightColumns"
          :data-source="filteredRightData"
          :pagination="false"
          :row-selection="rightRowSelection"
          v-model:selectedRowKeys="rightSelectedRowKeys"
          row-key="field_name"
          size="small"
          @row-drag-end="onRowDragEnd"
        />
      </div>
    </div>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, watch, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { RightOutlined, LeftOutlined } from '@ant-design/icons-vue';
import { SearchConfig, ViewSearchItem } from '../typing';
import { cloneDeep } from 'lodash';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  searchConfig: {
    type: Array as PropType<SearchConfig[]>,
    default: () => [],
  },
  viewSearchConfig: {
    type: Object as PropType<Record<string, ViewSearchItem>>,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:open', 'change']);

const { t } = useI18n();

const visible = ref(false);
const leftSearch = ref('');
const rightSearch = ref('');
const leftSelectedRowKeys = ref<string[]>([]);
const rightSelectedRowKeys = ref<string[]>([]);
const rightData = ref<SearchConfig[]>([]);

const leftColumns = [
  {
    title: t('字段名称'),
    dataIndex: 'label',
    key: 'label',
    width: 250,
    ellipsis: true,
  },
];

const rightColumns = computed(() => {
  const columns: any = [
    {
      title: t('字段名称'),
      dataIndex: 'label',
      key: 'label',
      rowDrag: true,
      width: 150,
      ellipsis: true,
    },
  ];
  if (filteredRightData.value.length > 0) {
    if (filteredRightData.value.some(item => item.group_name)) {
      columns.push({
        title: t('分组'),
        dataIndex: 'group_name',
        key: 'group_name',
        width: 150,
        ellipsis: true,
      });
    }
  }
  return columns;
});

watch(
  () => props.open,
  val => {
    visible.value = val;
    if (val) {
      leftSearch.value = '';
      rightSearch.value = '';
      initData();
    }
  },
);

watch(visible, val => {
  emit('update:open', val);
});

const initData = () => {
  // 根据 viewSearchConfig 和 searchConfig 初始化右侧数据
  const configMap = new Map();
  props.searchConfig.forEach(item => {
    configMap.set(item.field_name, item);
  });

  const selectedFields: SearchConfig[] = [];

  if (props.viewSearchConfig && Object.keys(props.viewSearchConfig).length > 0) {
    // 先处理 viewSearchConfig 中已有的配置
    Object.keys(props.viewSearchConfig).forEach(fieldName => {
      const viewItem = props.viewSearchConfig[fieldName];
      if (viewItem.checked && configMap.has(fieldName)) {
        selectedFields.push({
          ...configMap.get(fieldName),
          order: viewItem.order ?? 0,
        });
      }
    });
    // 排序
    selectedFields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  } else {
    // 当 viewSearchConfig 为空时，所有 item.checked !== false 的字段都是选中字段
    props.searchConfig.forEach((item, index) => {
      if (item.checked !== false) {
        selectedFields.push({
          ...item,
          order: index + 1,
        });
      }
    });
  }

  rightData.value = cloneDeep(selectedFields);

  leftSelectedRowKeys.value = [];
  rightSelectedRowKeys.value = [];
};

const leftData = computed(() => {
  const selectedKeys = new Set(rightData.value.map(item => item.field_name));
  const groups: Record<string, any[]> = {};
  const noGroup: any[] = [];

  props.searchConfig.forEach(item => {
    const itemData = {
      ...item,
      is_selected: selectedKeys.has(item.field_name),
    };
    if (item.group_name) {
      if (!groups[item.group_name]) {
        groups[item.group_name] = [];
      }
      groups[item.group_name].push(itemData);
    } else {
      noGroup.push(itemData);
    }
  });

  const result: any[] = [];
  Object.keys(groups).forEach(groupName => {
    result.push({
      field_name: `group_${groupName}`,
      label: groupName,
      is_group: true,
      children: groups[groupName],
    });
  });

  return [...result, ...noGroup];
});

const filteredLeftData = computed(() => {
  if (!leftSearch.value) return leftData.value;
  const search = leftSearch.value.toLowerCase();

  const filterNode = (nodes: any[]) => {
    return nodes.reduce((acc: any[], node: any) => {
      const matchSelf =
        node.label?.toLowerCase().includes(search) ||
        node.field_name?.toLowerCase().includes(search);

      if (node.children) {
        const filteredChildren = filterNode(node.children);
        if (filteredChildren.length > 0) {
          acc.push({ ...node, children: filteredChildren });
        } else if (matchSelf) {
          acc.push({ ...node, children: [] });
        }
      } else if (matchSelf) {
        acc.push(node);
      }
      return acc;
    }, []);
  };

  return filterNode(leftData.value);
});

const filteredRightData = computed(() => {
  if (!rightSearch.value) return rightData.value;
  const search = rightSearch.value.toLowerCase();
  return rightData.value.filter(
    item =>
      item.label?.toLowerCase().includes(search) || item.field_name?.toLowerCase().includes(search),
  );
});

const leftRowSelection = {
  onChange: (selectedRowKeys: string[]) => {
    leftSelectedRowKeys.value = selectedRowKeys;
  },
  getCheckboxProps: (record: any) => ({
    disabled: record.is_group || record.is_selected,
  }),
};

const rightRowSelection = {
  onChange: (selectedRowKeys: string[]) => {
    rightSelectedRowKeys.value = selectedRowKeys;
  },
};

const moveToRight = () => {
  const selectedKeys = new Set(leftSelectedRowKeys.value);
  const toMove: SearchConfig[] = [];

  // 遍历所有 searchConfig，按原始顺序找到匹配项
  props.searchConfig.forEach(item => {
    if (selectedKeys.has(item.field_name)) {
      toMove.push(item);
    }
  });

  if (toMove.length > 0) {
    rightData.value = [...rightData.value, ...cloneDeep(toMove)];
  }
  leftSelectedRowKeys.value = [];
};

const moveToLeft = () => {
  rightData.value = rightData.value.filter(
    item => !rightSelectedRowKeys.value.includes(item.field_name),
  );
  rightSelectedRowKeys.value = [];
};

const onRowDragEnd = ({ oldIndex, newIndex }) => {
  const target = rightData.value.splice(oldIndex, 1)[0];
  rightData.value.splice(newIndex, 0, target);
};

const handleOk = () => {
  const result: Record<string, ViewSearchItem> = {};

  // 处理所有字段，默认为未选中
  props.searchConfig.forEach(item => {
    result[item.field_name] = {
      checked: false,
      order: 0,
    };
  });

  // 更新已选中的字段
  rightData.value.forEach((item, index) => {
    result[item.field_name] = {
      checked: true,
      order: index + 1,
    };
  });

  emit('change', result);
  visible.value = false;
};

const handleCancel = () => {
  visible.value = false;
};
</script>

<style lang="less" scoped>
.search-config-container {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 70vh;
  .table-wrapper {
    width: 450px;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: auto;

    .table-title {
      padding: 8px 12px;
      background: #fafafa;
      border-bottom: 1px solid #f0f0f0;
      font-weight: bold;
    }
  }

  .middle-btns {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
}
</style>
